/**
 * Policies: how each person wants the agents that work for them to behave.
 *
 * A policy is one key with a typed value. Values resolve as the code preset here, then the firm's default (tenant
 * admins can lock it so no one overrides it), then the person's own value. Agents read the resolved values once per
 * run: prompt text shapes what they write, and code reads typed values such as working hours. A policy never grants
 * permission for anything; approvals, roles and tenant isolation stay in code.
 */
import { z } from 'zod';
import type { AgentSlug } from './agents';
import type { UserRole } from './rbac';

export const POLICY_KEYS = ['reply_style', 'writing_style', 'about_me', 'standing_instructions', 'working_hours', 'research_depth'] as const;
export type PolicyKey = (typeof POLICY_KEYS)[number];

/** Who has policies of their own: everyone who works with the agents, never a portfolio company or an external user. */
export const POLICY_ROLES: readonly UserRole[] = ['super_admin', 'admin', 'billing_admin', 'user'];

/** An empty object means the agent's own default. */
export const ReplyStyleSchema = z
  .object({
    length: z.enum(['brief', 'detailed']).optional(),
    format: z.enum(['bullets', 'prose']).optional(),
  })
  .strict();

/** For what agents write to other people in this person's name, such as email drafts and HubSpot notes. */
export const WritingStyleSchema = z
  .object({
    tone: z.enum(['warm', 'neutral', 'formal', 'casual', 'direct']).optional(),
    maxWords: z.number().int().min(40, 'Use 40 to 400 words').max(400, 'Use 40 to 400 words').optional(),
    greeting: z.string().trim().min(1).max(60).optional(),
    signOff: z.string().trim().min(1).max(80).optional(),
  })
  .strict();

export const AboutMeSchema = z
  .object({
    role: z.string().trim().min(1).max(100).optional(),
    focus: z.string().trim().min(1).max(300).optional(),
    note: z.string().trim().min(1).max(500).optional(),
  })
  .strict();

export const StandingInstructionsSchema = z.array(z.string().trim().min(1).max(200)).max(10);

/** Whether `name` is an IANA time zone this runtime knows, such as America/New_York. */
function isTimeZone(name: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: name });
    return true;
  } catch {
    return false;
  }
}

/**
 * 1 is Monday, 7 is Sunday; the hour is local. The time zone is optional: without one, the person's users.time_zone
 * (set when they connect HubSpot) applies.
 */
export const WorkingHoursSchema = z
  .object({
    timeZone: z.string().refine(isTimeZone, 'Not a time zone').optional(),
    workdays: z
      .array(z.number().int().min(1).max(7))
      .min(1, 'Pick at least one workday')
      .max(7)
      .refine((days) => new Set(days).size === days.length, 'Each day once'),
    startHour: z.number().int().min(0).max(23),
  })
  .strict();

export const ResearchDepthSchema = z.enum(['quick', 'standard', 'deep']);

export interface PolicyValues {
  reply_style: z.infer<typeof ReplyStyleSchema>;
  writing_style: z.infer<typeof WritingStyleSchema>;
  about_me: z.infer<typeof AboutMeSchema>;
  standing_instructions: z.infer<typeof StandingInstructionsSchema>;
  working_hours: z.infer<typeof WorkingHoursSchema>;
  research_depth: z.infer<typeof ResearchDepthSchema>;
}

export const POLICY_SCHEMAS: { [K in PolicyKey]: z.ZodType<PolicyValues[K]> } = {
  reply_style: ReplyStyleSchema,
  writing_style: WritingStyleSchema,
  about_me: AboutMeSchema,
  standing_instructions: StandingInstructionsSchema,
  working_hours: WorkingHoursSchema,
  research_depth: ResearchDepthSchema,
};

/** What applies when neither the firm nor the person set a value: what the agents did before policies existed. */
export const POLICY_PRESETS: PolicyValues = {
  reply_style: {},
  writing_style: {},
  about_me: {},
  standing_instructions: [],
  working_hours: { workdays: [1, 2, 3, 4, 5], startHour: 8 },
  research_depth: 'standard',
};

/**
 * The keys whose value adds to the firm's: a style's fields one by one, and standing instructions one after another.
 * For every other key the person's value, when the firm did not lock the key, replaces the firm's whole.
 */
export const POLICY_KEYS_ADDING_TO_FIRM: readonly PolicyKey[] = ['reply_style', 'writing_style', 'about_me', 'standing_instructions'];

/** The agents whose prompt carries each policy. Code reads working_hours and research_depth too, wherever they apply. */
export const POLICY_AGENTS: { [K in PolicyKey]: readonly AgentSlug[] } = {
  reply_style: ['manager', 'crm'],
  writing_style: ['bdr'],
  about_me: ['manager', 'crm', 'bdr', 'research'],
  standing_instructions: ['manager', 'crm', 'bdr', 'research', 'analytics'],
  working_hours: ['manager', 'bdr'],
  research_depth: ['research'],
};

/**
 * Where a card asking to remove one of the person's preferences stands. A preference is removed from chat only when its
 * owner clicks Remove on the card; `replaced` means a newer card took its place, `changed` that the value had changed.
 */
export type RemovalStatus = 'pending' | 'removed' | 'kept' | 'replaced' | 'changed' | 'expired';

/** A removal card as the web chat and Slack show it. */
export interface RemovalCard {
  id: string;
  /** What the card says in its current state, such as Remove your sign-off "Mal"? */
  text: string;
  status: RemovalStatus;
}

/** The keys a value learned from how a person works can be proposed for, in the order the learner tries them. */
export const LEARNABLE_KEYS = ['writing_style', 'reply_style', 'working_hours'] as const;
export type LearnableKey = (typeof LEARNABLE_KEYS)[number];

/**
 * Where a proposal of a learned value stands. Only its own person's Use this makes it `accepted`; `declined` is their
 * Not now, `changed` means their value had changed since it was proposed, and `withdrawn` that their firm switched
 * learning off.
 */
export type ProposalStatus = 'pending' | 'accepted' | 'declined' | 'changed' | 'expired' | 'withdrawn';

/** A proposal as Settings and Slack show it, built from what was stored, never from a model's words. */
export interface ProposalCard {
  id: string;
  /** What it asks in its current state, such as Keep my replies to you brief? */
  text: string;
  /** Why it is suggested: counts, dates and the person's own words. */
  evidence: string[];
  status: ProposalStatus;
}

/** One policy as the person sees it: the firm's default, whether it is locked, their own value, and what applies. */
export interface MyPolicy<K extends PolicyKey = PolicyKey> {
  firm: PolicyValues[K] | null;
  locked: boolean;
  mine: PolicyValues[K] | null;
  resolved: PolicyValues[K];
}

export type MyPolicies = { [K in PolicyKey]: MyPolicy<K> };

/** One firm default as admins see it. */
export interface FirmPolicy<K extends PolicyKey = PolicyKey> {
  value: PolicyValues[K] | null;
  locked: boolean;
}

export type FirmPolicies = { [K in PolicyKey]: FirmPolicy<K> };
